import React from 'react';

export default function ActionPanel({ actionLevel }) {
  if (!actionLevel) return null;

  const exposurePercent = parseInt(actionLevel.exposure) || 0;

  return (
    <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-lg text-mentor-text">
          So What Do I Do?
        </h3>
        <span
          className="action-badge"
          style={{
            backgroundColor: `${actionLevel.color}18`,
            color: actionLevel.color,
            border: `1px solid ${actionLevel.color}40`,
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: actionLevel.color }}
          />
          {actionLevel.label}
        </span>
      </div>

      {/* Recommended Exposure Bar */}
      <div className="mb-5">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-mentor-muted font-body">Recommended Market Exposure</span>
          <span className="font-mono font-semibold" style={{ color: actionLevel.color }}>
            {actionLevel.exposure}
          </span>
        </div>
        <div className="w-full h-3 bg-mentor-bg rounded-full overflow-hidden border border-mentor-border">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${Math.max(exposurePercent, 5)}%`,
              background: `linear-gradient(90deg, ${actionLevel.color}cc, ${actionLevel.color})`,
              boxShadow: `0 0 12px ${actionLevel.color}40`,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-mentor-muted mt-1 font-mono">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Action Description */}
      <p className="text-mentor-text/90 font-body leading-relaxed text-[0.9rem]">
        {actionLevel.description}
      </p>

      {/* Quick Action Items */}
      <div className="mt-5 pt-4 border-t border-mentor-border/50">
        <h4 className="text-sm font-display font-semibold text-mentor-muted mb-3 uppercase tracking-wider">
          Action Checklist
        </h4>
        <div className="space-y-2">
          {getActionItems(actionLevel.key).map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="mt-0.5 text-xs" style={{ color: actionLevel.color }}>●</span>
              <span className="text-sm text-mentor-text/90 font-body">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getActionItems(levelKey) {
  const items = {
    FULLY_INVESTED: [
      'Maintain current positions — no changes needed',
      'Continue regular investment contributions (dollar-cost averaging)',
      'Review and rebalance on your normal schedule',
    ],
    TIGHTEN_STOPS: [
      'Review your stop-loss levels and tighten them by 3-5%',
      'Hold off on adding new speculative positions',
      'Ensure you have your target cash reserve',
    ],
    REDUCE_75: [
      'Sell your weakest 25% of positions and move to cash',
      'Prioritize exiting small or speculative holdings first',
      'Set tight stops on remaining positions',
      'Monitor daily — signals could escalate',
    ],
    REDUCE_50: [
      'Sell another 25% of positions (50% total in cash)',
      'Keep only your highest-conviction, lowest-beta positions',
      'Consider short-term Treasury bills or money market funds',
      'Do NOT buy any dips until signals improve',
    ],
    DEFENSIVE_25: [
      'Move to 75%+ cash and short-term Treasuries',
      'Only hold positions you would not sell at any price',
      'Avoid long-duration bonds — they will fall if yields spike',
      'Watch for signal improvements before re-entering',
    ],
    MAX_DEFENSE: [
      'Move to near-100% cash or ultra-short Treasury bills',
      'This is a rare, extreme signal — take it seriously',
      'Wait for confirmed signal improvement before any buying',
      'Consider this a capital preservation emergency',
    ],
  };

  return items[levelKey] || items.FULLY_INVESTED;
}
